import { useLogger } from '../../composables/useLogger'
import { useSupabaseClientStore } from '../../stores/supaBaseClientStore'
import { Image } from './Image'
import { getUserId } from '../utils/userUtils'
import type { ImageUpload } from './ImageUpload';

const logger = useLogger()

interface ImageGameData {
    id: string;
    created_at: string | null;
    image_id: string | null;
    game_type: string | null;
    saved_state: Record<string, any> | null;
    name: string | null;
    sort_order: number;
}

export class ImageGame {
    id: string;
    created_at: string | null;
    image_id: string | null;
    image: Image | null;
    game_type: string | null;
    user_id: string | null;
    saved_state: Record<string, any> | null;
    name: string;
    sort_order: number;

    constructor(id: string) {
        this.id = id;
        this.created_at = null;
        this.image_id = null;
        this.image = null;
        this.game_type = null;
        this.user_id = null;
        this.saved_state = null;
        this.name = "";
        this.sort_order = 9999;
    }

    // static method to get the supabase client
    static getSupabase() {
        const supabaseClientStore = useSupabaseClientStore()
        return supabaseClientStore.supabase;
    }

    /**
     * Create an image game from a database row.
     * @param {ImageGameData} row - The row to create the image game from.
     * @param {boolean} withBlob - Whether to populate the blob.
     * @returns {Promise<ImageGame>} The image game.
     */
    static async fromDatabaseRow(row: ImageGameData, withBlob: boolean = true): Promise<ImageGame> {
        const game = new ImageGame(row.id);
        game.created_at = row.created_at;
        game.image_id = row.image_id;
        game.game_type = row.game_type;
        if (game.image_id) {
            game.image = await Image.fromDatabase(game.image_id);
        }
        game.saved_state = row.saved_state;
        game.name = row.name || "";
        game.sort_order = row.sort_order;
        return game;
    }

    /**
     * Get an image game from the database.
     * @param {string} gameId - The id of the game to get.
     * @param {boolean} withBlob - Whether to populate the blob.
     * @returns {Promise<ImageGame>} The image game.
     */
    static async fromId(gameId: string, withBlob: boolean = true): Promise<ImageGame> {
        const { data, error } = await ImageGame.getSupabase()
            .from('imagegames')
            .select('*')
            .eq('id', gameId)
            .single();
        if (error) logger.throwSupabaseError(error, `Failed to get image game with ID ${gameId}`);
        const game = await ImageGame.fromDatabaseRow(data as ImageGameData, withBlob);
        return game;
    }

    /**
     * Get all image games from the database.
     * @param {boolean} withBlob - Whether to populate the blob.
     * @returns {Promise<ImageGame[]>} The image games.
     */
    static async allFromDatabase(withBlob: boolean = true): Promise<ImageGame[]> {
        const { data, error } = await ImageGame.getSupabase()
            .from('imagegames')
            .select('*')
            .eq('user_id', getUserId());
        if (error) {
            logger.error('Error getting games from database:', error);
            logger.throwSupabaseError(error, 'Failed to get all image games');
        }
        logger.debug('games from database:', { data: data });
        const games: ImageGame[] = [];
        for (const row of data) {
            const game = await ImageGame.fromDatabaseRow(row as ImageGameData, withBlob);
            games.push(game);
        }
        return games;
    }

    /**
     * Create an image game from an image upload.
     * @param {ImageUpload[]} imageUploads - The images to add to the game.
     * @param {string} game_type - The type of game to create.
     * @returns {Promise<ImageGame>} The new image game.
     */
    static async createImageGamesFromUpload(imageUploads: ImageUpload[], game_type: string): Promise<ImageGame[]> {
        logger.debug('Creating a game from an image upload:', { game_type: game_type });

        const newImageGames: ImageGame[] = [];

        for (const imageUpload of imageUploads) {
            const newImage = await Image.createFromUpload(imageUpload);

            const { data, error } = await ImageGame.getSupabase()
                .from('imagegames')
                .insert({
                    user_id: getUserId(),
                    game_type: game_type,
                    image_id: newImage.id,
                    saved_state: '{}',
                    name: "",
                    sort_order: 9999
                })
                .select()
                .single();

            if (error) logger.throwSupabaseError(error, `Failed to create image game for image ${newImage.id}`);

            const newImageGame = await ImageGame.fromDatabaseRow(data);
            newImageGames.push(newImageGame);
        }
        return newImageGames;
    }

    /**
     * Delete an image game from the database.
     * @param {string} gameId - The id of the game to delete.
     * @returns {Promise<void>}
     */
    static async deleteGame(gameId: string): Promise<void> {
        logger.debug('Deleting game:', { toDeleteGame: gameId });
        const tempGame = await ImageGame.fromId(gameId);
        logger.debug('tempGame:', { tempGame: tempGame });

        const { error } = await ImageGame.getSupabase()
            .from('imagegames')
            .delete()
            .eq('id', tempGame.id);
        if (error) logger.throwSupabaseError(error, `Failed to delete image game ${gameId}`);

        if (tempGame.image_id) {
            await Image.deleteImage(tempGame.image_id);
        }
    }

    /**
     * Delete all image games from the database.
     * @returns {Promise<void>}
     */
    static async deleteAllGames(): Promise<void> {
        const { data, error } = await ImageGame.getSupabase()
            .from('imagegames')
            .select('*')
            .eq('user_id', getUserId());
        if (error) logger.throwSupabaseError(error, 'Failed to get all image games for deletion');
        for (const game of data) {
            await ImageGame.deleteGame(game.id);
        }
    }

    /**
     * Update this image game in the database.
     * @returns {Promise<void>}
     */
    async update(): Promise<void> {
        logger.debug('Updating game:', { game: this });
        const { error } = await ImageGame.getSupabase()
            .from('imagegames')
            .update({
                game_type: this.game_type,
                saved_state: this.saved_state,
                image_id: this.image_id,
                name: this.name,
                sort_order: this.sort_order
            })
            .eq('id', this.id);

        if (error) {
            logger.throwSupabaseError(error, `Failed to update image game ${this.id}`);
        }
    }

    /**
     * Update the name of this image game.
     * @param {string} newName - The new name for the game.
     * @returns {Promise<void>}
     */
    async updateName(newName: string): Promise<void> {
        logger.debug('Updating game name:', { gameId: this.id, newName: newName });
        const { error } = await ImageGame.getSupabase()
            .from('imagegames')
            .update({ name: newName })
            .eq('id', this.id);

        if (error) {
            logger.throwSupabaseError(error, `Failed to update name for image game ${this.id}`);
        }
        this.name = newName;
    }

    /**
     * Update the sort order of this image game.
     * @param {number} newSortOrder - The new sort order for the game.
     * @returns {Promise<void>}
     */
    async updateSortOrder(newSortOrder: number): Promise<void> {
        logger.debug('Updating game sort order:', { gameId: this.id, newSortOrder: newSortOrder });
        const { error } = await ImageGame.getSupabase()
            .from('imagegames')
            .update({ sort_order: newSortOrder })
            .eq('id', this.id);

        if (error) {
            logger.throwSupabaseError(error, `Failed to update sort order for image game ${this.id}`);
        }
        this.sort_order = newSortOrder;
    }

    /**
     * Batch update sort orders for multiple games.
     * @param {Array<{id: string, sort_order: number}>} updates - Array of updates with game IDs and new sort orders.
     * @returns {Promise<void>}
     */
    static async batchUpdateSortOrder(updates: Array<{ id: string, sort_order: number }>): Promise<void> {
        logger.debug('Batch updating game sort orders:', { updates });

        // First, get the existing games to preserve their data
        const { data: existingGames, error: fetchError } = await ImageGame.getSupabase()
            .from('imagegames')
            .select('*')
            .in('id', updates.map(u => u.id));

        if (fetchError) {
            logger.throwSupabaseError(fetchError, 'Failed to fetch games for batch update');
        }

        // Create a map of existing game data
        const gameMap = new Map(existingGames.map(game => [game.id, game]));

        // Prepare updates with all required fields
        const updatesWithAllFields = updates.map(update => {
            const existingGame = gameMap.get(update.id);
            return {
                id: update.id,
                sort_order: update.sort_order,
                game_type: existingGame.game_type,
                image_id: existingGame.image_id,
                saved_state: existingGame.saved_state || '{}',
                name: existingGame.name || '',
                user_id: existingGame.user_id
            };
        });

        const { error } = await ImageGame.getSupabase()
            .from('imagegames')
            .upsert(updatesWithAllFields);

        if (error) {
            logger.error('Error batch updating game sort orders:', error);
            logger.throwSupabaseError(error, 'Failed to batch update image game sort orders');
        }
    }

    /**
     * Get all image ids in imagegames for a user - for use in user deletion
     * @returns {Promise<string[]>} The image ids.
     */
    static async getAllImageGameImageIdsForUser(userId: string): Promise<string[]> {
        const { data, error } = await ImageGame.getSupabase()
            .from('imagegames')
            .select('id')
            .eq('user_id', userId)
        if (error) logger.throwSupabaseError(error, `Failed to get all image game IDs for user ${userId}`);
        return data.map(row => row.id)
    }

    /**
     * Delete all image games for a user - for use in user deletion.
     * @param {string} userId - The id of the user to delete the image games for.
     * @returns {Promise<void>}
     */
    static async deleteAllImageGamesForUser(userId: string): Promise<void> {
        try {
            const imageGameIds = await ImageGame.getAllImageGameImageIdsForUser(userId)
            logger.debug('imageGameIds to delete:', { imageGameIds: imageGameIds })

            logger.debug('deleting image games...')
            for (const imageGameId of imageGameIds) {
                await ImageGame.deleteGame(imageGameId)
            }
            logger.debug('image games deleted')
        } catch (error) {
            logger.throwSupabaseError(error, `Failed to delete all image games for user ${userId}`);
        }
    }
}